<form
  [formGroup]="carePlanForm"
  (ngSubmit)="submitForm($event)"
  [ngClass]="{
    'plan-submitted': carePlanSubmitted,
    'plan-not-submitted': !carePlanSubmitted,
  }"
>
  <nz-layout class="care-plan-editor">
    <nz-header>
      <div class="editor-header">
        <div class="editor-title">
          {{ editorTitle | translate }}
        </div>
        <div class="editor-actions">
          @if (!isReadOnly) {
            <button
              nz-button
              nzPrimary
              class="save-btn"
              type="submit"
              [nzLoading]="isLoading || isDropdownLoading"
              [disabled]="isLoading || isDropdownLoading"
            >
              {{ "Save" | translate }}
            </button>
          }
          <button
            nz-button
            [nzDanger]="!isReadOnly"
            class="cancel-btn"
            type="button"
            (click)="handleCancel()"
            [disabled]="isLoading"
          >
            {{ cancelLabel | translate }}
          </button>
        </div>
      </div>
    </nz-header>

    <nz-content>
      <nz-tabset nzType="card">
        <nz-tab [nzTitle]="'Summary' | translate">
          <nz-layout class="table-margin margin-15 summary-section">
            <nz-content class="table-content summary-content">
              <div fxLayout="row" fxLayout.lt-md="column" class="form-grid">
                <div fxFlex="50" class="field">
                  <label>{{ "Title" | translate }}</label>
                  <input
                    nz-input
                    class="width-100 plan"
                    formControlName="title"
                    [placeholder]="
                      isReadOnly
                        ? ''
                        : ('e.g. Heart failure discharge and follow-up plan'
                          | translate)
                    "
                    type="text"
                  />
                </div>
                <div fxFlex="25" class="field">
                  <label>{{ "Status" | translate }}</label>
                  <nz-select
                    (keydown.enter)="
                      $event.stopPropagation(); $event.preventDefault()
                    "
                    nzShowSearch
                    nzAllowClear
                    class="width-100 plan"
                    [nzPlaceHolder]="'Select status' | translate"
                    formControlName="statusId"
                    [nzLoading]="isDropdownLoading"
                  >
                    @for (option of dropdownData?.statuses; track option.id) {
                      <nz-option
                        [nzLabel]="option.display"
                        [nzValue]="option.id"
                      ></nz-option>
                    }
                  </nz-select>
                </div>

                <div fxFlex="25" class="field">
                  <label>{{ "Intent" | translate }}</label>
                  <nz-select
                    (keydown.enter)="
                      $event.stopPropagation(); $event.preventDefault()
                    "
                    nzShowSearch
                    nzAllowClear
                    class="width-100 plan"
                    [nzPlaceHolder]="'Select intent' | translate"
                    formControlName="intentId"
                    [nzLoading]="isDropdownLoading"
                  >
                    @for (option of dropdownData?.intents; track option.id) {
                      <nz-option
                        [nzLabel]="option.display"
                        [nzValue]="option.id"
                      ></nz-option>
                    }
                  </nz-select>
                </div>
              </div>

              <div fxLayout="row" fxLayout.lt-md="column" class="form-grid">
                <div fxFlex="33" class="field">
                  <label>{{ "Period" | translate }}</label>
                  <nz-range-picker
                    (keydown.enter)="
                      $event.stopPropagation(); $event.preventDefault()
                    "
                    class="width-100"
                    formControlName="period"
                    [nzPlaceHolder]="
                      isReadOnly
                        ? ['', '']
                        : ['Start date' | translate, 'End date' | translate]
                    "
                  ></nz-range-picker>
                  @if (hasInvalidPeriod) {
                    <div class="validation-error">
                      {{
                        "Period end must be after or equal to period start"
                          | translate
                      }}
                    </div>
                  }
                  @if (hasPastStartDateError) {
                    <div class="validation-error">
                      {{
                        "Care plan start date cannot be in the past" | translate
                      }}
                    </div>
                  }
                </div>
                <div fxFlex="33" class="field">
                  <label>{{ "Category" | translate }}</label>
                  <nz-select
                    (keydown.enter)="
                      $event.stopPropagation(); $event.preventDefault()
                    "
                    nzShowSearch
                    nzAllowClear
                    class="width-100"
                    [nzPlaceHolder]="
                      isReadOnly ? '' : ('Select category' | translate)
                    "
                    formControlName="categoryId"
                    [nzLoading]="isDropdownLoading"
                  >
                    @for (option of dropdownData?.categories; track option.id) {
                      <nz-option
                        [nzLabel]="option.display"
                        [nzValue]="option.id"
                      ></nz-option>
                    }
                  </nz-select>
                </div>
              </div>

              <div class="field">
                <label>{{ "Clinical description" | translate }}</label>
                <textarea
                  rows="3"
                  nz-input
                  class="width-100"
                  formControlName="description"
                  [placeholder]="
                    isReadOnly
                      ? ''
                      : ('Briefly describe what should happen next for this patient'
                        | translate)
                  "
                ></textarea>
              </div>

              <div class="field">
                <label>{{ "Notes" | translate }}</label>
                <textarea
                  rows="3"
                  nz-input
                  class="width-100"
                  formControlName="notes"
                ></textarea>
              </div>
            </nz-content>
          </nz-layout>
        </nz-tab>

        <nz-tab [nzTitle]="'Appointments' | translate">
          @if (canModifyActivities) {
            <div class="activity-toolbar">
              <button
                nz-button
                type="button"
                (click)="openAppointmentLinkModal()"
              >
                {{ "Link existing appointment" | translate }}
              </button>
              <button
                nz-button
                nzPrimary
                type="button"
                (click)="openAppointmentCreateModal()"
              >
                {{ "Create new appointment" | translate }}
              </button>
            </div>
          }

          <nz-table
            nzTableLayout="fixed"
            nzSize="middle"
            [nzData]="linkedAppointmentRows"
            [nzLoading]="isActivitiesLoading"
            [nzShowPagination]="false"
          >
            <thead>
              <tr>
                <th>{{ "State" | translate }}</th>
                <th>{{ "Status" | translate }}</th>
                <th>{{ "Service type" | translate }}</th>
                <th>{{ "Reason" | translate }}</th>
                <th>{{ "Next slot" | translate }}</th>
                <th>{{ "Last slot end" | translate }}</th>
                <th>{{ "Description" | translate }}</th>
                @if (showViewActions) {
                  <th>{{ "Actions" | translate }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of linkedAppointmentRows; track row.uiId) {
                <tr>
                  <td>{{ row.activityState }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.serviceType }}</td>
                  <td>{{ row.reason }}</td>
                  <td>{{ row.nextSlotStart }}</td>
                  <td>{{ row.lastSlotEnd }}</td>
                  <td>{{ row.description }}</td>
                  @if (showViewActions) {
                    <td class="inline-actions">
                      <span class="inline-actions-group">
                        @if (isReadOnly) {
                          <i
                            nz-icon
                            nzType="eye"
                            nzTheme="outline"
                            class="view-btn"
                            nz-tooltip
                            [nzTooltipTitle]="'View' | translate"
                            nzTooltipPlacement="top"
                            (click)="viewAppointmentRow(row)"
                          ></i>
                        } @else {
                          <i
                            nz-icon
                            nzType="icons:form"
                            nzTheme="outline"
                            class="edit-btn"
                            nz-tooltip
                            [nzTooltipTitle]="'Edit' | translate"
                            nzTooltipPlacement="top"
                            (click)="editAppointmentRow(row)"
                          ></i>
                          <i
                            nz-icon
                            nzType="icons:delete"
                            nzTheme="outline"
                            class="delete-btn"
                            nz-tooltip
                            [nzTooltipTitle]="'Delete' | translate"
                            nzTooltipPlacement="top"
                            (click)="removeAppointmentRow(row)"
                          ></i>
                        }
                      </span>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-tab>

        <nz-tab [nzTitle]="'Medications' | translate">
          @if (canModifyActivities) {
            <div class="activity-toolbar">
              <button
                nz-button
                type="button"
                (click)="openMedicationLinkModal()"
              >
                {{ "Link active medication" | translate }}
              </button>
              <button
                nz-button
                nzPrimary
                type="button"
                (click)="openMedicationAddModal()"
              >
                {{ "Create planned medication" | translate }}
              </button>
            </div>
          }

          <nz-table
            nzTableLayout="fixed"
            nzSize="middle"
            [nzData]="linkedMedicationRows"
            [nzLoading]="isActivitiesLoading"
            [nzShowPagination]="false"
          >
            <thead>
              <tr>
                <th>{{ "State" | translate }}</th>
                <th>{{ "Action" | translate }}</th>
                <th>{{ "Medication" | translate }}</th>
                <th>{{ "Timing" | translate }}</th>
                <th>{{ "Assigned doctor" | translate }}</th>
                @if (showViewActions) {
                  <th>{{ "Actions" | translate }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of linkedMedicationRows; track row.uiId) {
                <tr>
                  <td>{{ row.activityState }}</td>
                  <td>{{ row.medicationActionType }}</td>
                  <td>{{ row.medication }}</td>
                  <td>{{ row.timing }}</td>
                  <td>{{ row.assignedDoctor }}</td>
                  @if (showViewActions) {
                    <td class="inline-actions">
                      <span class="inline-actions-group">
                        @if (isReadOnly) {
                          <i
                            nz-icon
                            nzType="eye"
                            nzTheme="outline"
                            class="view-btn"
                            nz-tooltip
                            [nzTooltipTitle]="'View' | translate"
                            nzTooltipPlacement="top"
                            (click)="viewMedicationRow(row)"
                          ></i>
                        } @else {
                          <i
                            nz-icon
                            nzType="icons:delete"
                            nzTheme="outline"
                            class="delete-btn"
                            nz-tooltip
                            [nzTooltipTitle]="'Delete' | translate"
                            nzTooltipPlacement="top"
                            (click)="removeMedicationRow(row)"
                          ></i>
                        }
                      </span>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-tab>

        <nz-tab [nzTitle]="'Service Requests' | translate">
          @if (canModifyActivities) {
            <div class="activity-toolbar">
              <button
                nz-button
                nzPrimary
                type="button"
                (click)="openServiceRequestAddModal()"
              >
                {{ "Create new service request" | translate }}
              </button>
            </div>
          }

          <nz-table
            nzTableLayout="fixed"
            nzSize="middle"
            [nzData]="linkedServiceRequestRows"
            [nzLoading]="isActivitiesLoading"
            [nzShowPagination]="false"
          >
            <thead>
              <tr>
                <th>{{ "State" | translate }}</th>
                <th>{{ "Status" | translate }}</th>
                <th>{{ "Intent" | translate }}</th>
                <th>{{ "Priority" | translate }}</th>
                <th>{{ "Category" | translate }}</th>
                <th>{{ "Procedure code" | translate }}</th>
                <th>{{ "Occurrence" | translate }}</th>
                <th>{{ "Description" | translate }}</th>
                @if (canModifyActivities) {
                  <th>{{ "Actions" | translate }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of linkedServiceRequestRows; track row.uiId) {
                <tr>
                  <td>{{ row.activityState }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.intent }}</td>
                  <td>{{ row.priority }}</td>
                  <td>{{ row.category }}</td>
                  <td>{{ row.procedure }}</td>
                  <td>{{ row.occurrence }}</td>
                  <td>{{ row.description }}</td>
                  @if (canModifyActivities) {
                    <td class="inline-actions">
                      <span class="inline-actions-group">
                        <i
                          nz-icon
                          nzType="icons:delete"
                          nzTheme="outline"
                          class="delete-btn"
                          nz-tooltip
                          [nzTooltipTitle]="'Delete' | translate"
                          nzTooltipPlacement="top"
                          (click)="removeServiceRequestRow(row)"
                        ></i>
                      </span>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </nz-table>
        </nz-tab>

        <nz-tab [nzTitle]="'Attachments' | translate">
          @if (!editCarePlanId && canManageAttachments) {
            <div class="component-note">
              {{
                "Attachments will be uploaded after the care plan is saved."
                  | translate
              }}
            </div>
          }

          @if (canViewAttachments) {
            <div class="doc-list">
              @if (canManageAttachments) {
                <div class="upload-row">
                  <button
                    nz-button
                    nzType="default"
                    type="button"
                    [disabled]="isAttachmentUploading"
                    (click)="attachmentFileInput.click()"
                  >
                    <i nz-icon nzType="upload" nzTheme="outline"></i>
                    {{ "Upload files" | translate }}
                  </button>
                  <input
                    #attachmentFileInput
                    type="file"
                    (change)="onAttachmentFileSelected($event)"
                    accept=".pdf,image/*"
                    multiple
                    hidden
                  />
                </div>
              }

              @if (!canManageAttachments) {
                <div class="component-note attachment-readonly-note">
                  {{
                    "Attachments are view-only for submitted, read-only, or non-owned care plans."
                      | translate
                  }}
                </div>
              }
              @if (isAttachmentsLoading) {
                <div class="component-note">
                  {{ "Loading attachments" | translate }}...
                </div>
              }

              @for (row of attachmentRows; track row.uiId) {
                <div class="doc-row">
                  <div class="left">
                    <i
                      nz-icon
                      nzType="file-pdf"
                      nzTheme="outline"
                      *ngIf="
                        (row.contentType || '').includes('pdf') ||
                        row.fileName.toLowerCase().endsWith('.pdf')
                      "
                    ></i>
                    <i
                      nz-icon
                      nzType="file-image"
                      nzTheme="outline"
                      *ngIf="(row.contentType || '').startsWith('image/')"
                    ></i>
                    <i
                      nz-icon
                      nzType="file"
                      nzTheme="outline"
                      *ngIf="
                        !(row.contentType || '').includes('pdf') &&
                        !(row.contentType || '').startsWith('image/')
                      "
                    ></i>

                    <div class="meta">
                      <div class="name" [title]="row.fileName">
                        {{ row.fileName }}
                      </div>
                      <div class="sub">
                        {{ formatAttachmentSize(row.size) }} |
                        {{
                          (row.isSubmitted ? "Submitted" : "Draft") | translate
                        }}
                      </div>
                      @if (row.description || row.date || row.createdByUserId) {
                        <div class="sub">
                          {{
                            row.description ||
                              row.createdByUserId ||
                              formatAttachmentDate(row.date)
                          }}
                        </div>
                      }
                    </div>
                  </div>

                  <div class="actions">
                    <button
                      nz-button
                      nzType="default"
                      nzShape="round"
                      class="action-btn"
                      type="button"
                      (click)="viewAttachment(row)"
                      [disabled]="!canPreviewAttachment(row)"
                    >
                      <i nz-icon nzType="eye" nzTheme="outline"></i>
                      <span class="btn-text">{{ "View" | translate }}</span>
                    </button>

                    @if (!row.isPendingUpload) {
                      <button
                        nz-button
                        nzType="default"
                        nzShape="round"
                        class="action-btn"
                        type="button"
                        (click)="downloadAttachment(row)"
                      >
                        <i nz-icon nzType="download" nzTheme="outline"></i>
                        <span class="btn-text">{{
                          "Download" | translate
                        }}</span>
                      </button>
                    }

                    @if (canManageAttachments) {
                      <button
                        nz-button
                        nzType="primary"
                        nzDanger
                        nzShape="round"
                        class="action-btn btn-danger-solid"
                        type="button"
                        (click)="deleteAttachment(row)"
                      >
                        <i nz-icon nzType="delete" nzTheme="outline"></i>
                        <span class="btn-text">{{ "Delete" | translate }}</span>
                      </button>
                    }
                  </div>
                </div>
              }

              @if (!isAttachmentsLoading && attachmentRows.length === 0) {
                <div class="empty">
                  <i nz-icon nzType="inbox" nzTheme="outline"></i>
                  <span>{{ "No files uploaded yet" | translate }}</span>
                </div>
              }
            </div>
          }
        </nz-tab>
      </nz-tabset>
    </nz-content>
  </nz-layout>
</form>

<app-add-calendar-event />
<app-view-calendar-event />

<nz-modal
  [(nzVisible)]="isAppointmentLinkVisible"
  [nzTitle]="'Link existing appointment' | translate"
  [nzFooter]="null"
  (nzOnCancel)="closeAppointmentLinkModal()"
>
  <ng-container *nzModalContent>
    <div class="linked-list">
      @if (isAvailableAppointmentsLoading) {
        <div class="component-note">
          {{ "Loading appointments" | translate }}...
        </div>
      }

      @for (
        appointment of availableAppointmentRows;
        track appointment.appointmentId
      ) {
        <div class="linked-row">
          <strong
            >{{ appointment.when }} - {{ appointment.serviceType }}</strong
          >
          <span>{{ appointment.reason }}</span>
          <span>{{ appointment.description }}</span>
          <button
            nz-button
            type="button"
            (click)="linkExistingAppointment(appointment)"
          >
            {{ "Link to CarePlan" | translate }}
          </button>
        </div>
      }

      @if (
        !isAvailableAppointmentsLoading && availableAppointmentRows.length === 0
      ) {
        <div class="component-note">
          {{ "No appointments found for the current patient" | translate }}
        </div>
      }
    </div>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="isMedicationLinkVisible"
  [nzTitle]="'Link active medication' | translate"
  [nzFooter]="null"
  (nzOnCancel)="closeMedicationLinkModal()"
>
  <ng-container *nzModalContent>
    <div class="linked-list">
      @if (isAvailableMedicationsLoading) {
        <div class="component-note">
          {{ "Loading medications" | translate }}...
        </div>
      }

      @for (
        medication of availableMedicationRows;
        track medication.medicationId
      ) {
        <div class="linked-row">
          <strong>{{ medication.medication }}</strong>
          <span>{{ medication.timing }}</span>
          <span>{{ medication.assignedDoctor }}</span>
          <div class="activity-toolbar">
            <button
              nz-button
              type="button"
              (click)="linkMedicationFromCurrent(medication, 'continue')"
            >
              {{ "Continue" | translate }}
            </button>
            <button
              nz-button
              type="button"
              (click)="linkMedicationFromCurrent(medication, 'stop')"
            >
              {{ "Stop" | translate }}
            </button>
          </div>
        </div>
      }

      @if (
        !isAvailableMedicationsLoading && availableMedicationRows.length === 0
      ) {
        <div class="component-note">
          {{ "No current medications found for the current visit" | translate }}
        </div>
      }
    </div>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="isServiceRequestAddVisible"
  [nzTitle]="'Create new service request' | translate"
  [nzFooter]="null"
  nzWidth="1200px"
  (nzOnCancel)="closeServiceRequestAddModal()"
>
  <ng-container *nzModalContent>
    <app-service-request-add
      (cancelled)="closeServiceRequestAddModal()"
      (created)="handleCreatedServiceRequest($event)"
    ></app-service-request-add>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="isMedicationAddVisible"
  [nzTitle]="medicationModalTitle | translate"
  [nzFooter]="null"
  nzWidth="2000px"
  (nzOnCancel)="closeMedicationAddModal()"
>
  <ng-container *nzModalContent>
    <app-medication-add />
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="isPdfPreviewVisible"
  [nzTitle]="
    ('Preview' | translate) +
    (pdfPreviewFileName ? ' - ' + pdfPreviewFileName : '')
  "
  [nzFooter]="null"
  nzWidth="80vw"
  [nzBodyStyle]="{ padding: '12px 16px 20px' }"
  (nzOnCancel)="closePdfPreview()"
>
  <ng-container *nzModalContent>
    @if (attachmentPreviewUrl) {
      <pdf-viewer
        class="attachment-pdf-viewer"
        [src]="attachmentPreviewUrl"
        [render-text]="true"
        [original-size]="false"
        [fit-to-page]="true"
        [show-all]="true"
      ></pdf-viewer>
    }
  </ng-container>
</nz-modal>
